import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import './index.scss';
import { getIsPadWidth } from '@elem/utils/devices';
import { useGlobal } from '@/utils/useGlobal';

export default function BlogCategoryItems({ categoryData = {}, categoryTagList = [] }: { categoryData: any, categoryTagList?: any }) {
    // 过滤掉 categoryData 中的 tag
    categoryTagList = categoryTagList.filter((item: any) => categoryData.categoryName !== item.tag);
    const [showDropdown, setShowDropdown] = useState(false);
    const [isPad, setIsPad] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const { locale = 'us' } = useRouter();
    const { useLang } = useGlobal();
    const { t } = useLang('blog');
    useEffect(() => {
        const checkPadWidth = () => {
            // 检测是否是小于等于1024端 (h5 + pad)
            setIsPad(getIsPadWidth());
        };
        checkPadWidth();
        window.addEventListener('resize', checkPadWidth);
        return () => window.removeEventListener('resize', checkPadWidth);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                if (!isPad) {
                    setShowDropdown(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isPad]);

    // 计算并设置CSS变量 --sticky-top-offset（仅初始化时）
    useEffect(() => {
        let totalHeight = 0;

        // 获取所有sticky元素的高度
        const header = document.querySelector('.ecoflow-frame-header');
        if (header) {
            totalHeight += header.getBoundingClientRect().height;
        }

        // 如果当前容器也是sticky的，加上它的高度
        if (containerRef.current) {
            const style = window.getComputedStyle(containerRef.current);
            if (style.position === 'sticky' || style.position === 'fixed') {
                totalHeight += containerRef.current.getBoundingClientRect().height;
            }
        }

        // 设置CSS变量到根元素
        document.documentElement.style.setProperty('--sticky-top-offset', `${totalHeight}px`);
    }, []);

    const MAX_VISIBLE_TAGS = 5;

    // 在最前面插入 Blog Center
    const blogCenterItem = { id: 'blog-center', tag: t('blogCenter'), tagUrl: '', isBlogCenter: true };
    const allTagsWithCenter = [blogCenterItem, ...categoryTagList];

    const visibleTags = isPad ? [] : allTagsWithCenter.slice(0, MAX_VISIBLE_TAGS);
    const hiddenTags = isPad ? allTagsWithCenter : allTagsWithCenter.slice(MAX_VISIBLE_TAGS);
    const hasMoreTags = hiddenTags.length > 0;

    const getCategoryUrl = (item: any) => {
        // Blog Center 跳转到博客首页
        if (item.id === 'blog-center') {
            return `/${locale}/blog`;
        }
        const slug = item.tagUrl;
        return `/${locale}/blog/category/${slug}`;
    };

    const handleAllClick = () => {
        if (isPad) {
            setShowModal(true);
        } else {
            setShowDropdown(!showDropdown);
        }
    };

    const handleModalItemClick = () => {
        setShowModal(false);
    };

    // 检查文本是否被截断，如果是则显示title
    const handleMouseEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
        const target = e.currentTarget;
        if (target.scrollWidth > target.clientWidth) {
            target.setAttribute('title', target.textContent || '');
        }
    };

    return (
        <>
            <div className="blog-category-nav-content" ref={containerRef}>
                <h1 className="blog-category-nav-content-title">
                    {categoryData.categoryName}
                </h1>
                {/* categoryTagList */}
                <div className="blog-category-nav-content-item">
                    {visibleTags.map((item: any, index: number) => {
                        return (
                            <a
                                key={index}
                                href={getCategoryUrl(item)}
                                className={`blog-category-nav-content-item-title-text ${item.isBlogCenter ? 'blog-center' : ''}`}
                                onMouseEnter={handleMouseEnter}
                            >
                                {item.isBlogCenter && (
                                    <img
                                        src="https://websiteoss.ecoflow.com/upload/image/1761041493108/Group%2012.svg"
                                        alt="Blog Center"
                                        className="blog-center-icon"
                                    />
                                )}
                                {item.tag}
                            </a>
                        );
                    })}

                    {hasMoreTags && (
                        <div
                            ref={dropdownRef}
                            className={`blog-category-nav-content-item-dropdown ${showDropdown ? 'active' : ''}`}
                            onMouseEnter={() => !isPad && setShowDropdown(true)}
                            onMouseLeave={() => !isPad && setShowDropdown(false)}
                            onClick={handleAllClick}
                        >
                            <div className="blog-category-nav-content-item-title-text">
                                {t('all')}
                                <svg
                                    className={`dropdown-arrow ${showDropdown ? 'open' : ''}`}
                                    width="18"
                                    height="18"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                >
                                    <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>

                            <div className="blog-category-nav-content-item-dropdown-menu">
                                {hiddenTags.map((item: any, index: number) => {
                                    return (
                                        <a
                                            key={index}
                                            href={getCategoryUrl(item)}
                                            className={`blog-category-nav-content-item-dropdown-menu-item ${item.isBlogCenter ? 'blog-center' : ''}`}
                                            onMouseEnter={handleMouseEnter}
                                        >
                                            {item.isBlogCenter && (
                                                <img
                                                    src="https://websiteoss.ecoflow.com/upload/image/1761041493108/Group%2012.svg"
                                                    alt="Blog Center"
                                                    className="blog-center-icon"
                                                />
                                            )}
                                            {item.tag}
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
                {/* Modal Overlay and Content */}
                {isPad && showModal && (
                    <>
                        <div className="blog-category-modal-overlay" onClick={() => setShowModal(false)}></div>
                        <div className="blog-category-modal">
                            <div className="blog-category-modal-header">
                                <h3>{categoryData.categoryName}</h3>
                                <button
                                    className="blog-category-modal-close"
                                    onClick={() => setShowModal(false)}
                                    aria-label="Close"
                                >
                                    ✕
                                </button>
                            </div>
                            <div className="blog-category-modal-content">
                                {hiddenTags.map((item: any, index: number) => {
                                    return (
                                        <a
                                            key={index}
                                            href={getCategoryUrl(item)}
                                            className={`blog-category-modal-item ${item.isBlogCenter ? 'blog-center' : ''}`}
                                            onClick={handleModalItemClick}
                                        >
                                            {item.isBlogCenter && (
                                                <img
                                                    src="https://websiteoss.ecoflow.com/upload/image/1761041493108/Group%2012.svg"
                                                    alt="Blog Center"
                                                    className="blog-center-icon"
                                                />
                                            )}
                                            <span>{item.tag}</span>
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    </>
                )}
            </div>

        </>
    );
}
